/* spring-package — denomination chip selection, sticky action bar, and
   self-contained AJAX add-to-cart that hooks the theme's cart drawer.
   Variant of spring-gift-card.js with the recipient/send-on logic removed
   and a discount-active guard so a fixed discounted price isn't overwritten
   by chip selection.

   Cart-drawer contract (shared with spring-pdp.js):
     - POST /cart/add.js          → adds the line item (id)
     - GET  /cart.js              → full cart state
     - dispatch "cart:changed"    → drawer refetches its contents
     - dispatch "cart:item-added" → drawer scrolls to the new item
     - click [data-action='show-cart-drawer'] → opens the drawer
*/
(function () {
  if (customElements.get('spring-package')) return;

  class SpringPackage extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-product-form]');
      this.priceDisplay = this.querySelector('[data-price-display]');
      // When a discounted price is shown it's a fixed metafield value — chip
      // selection must not overwrite it.
      this.discountActive = this.dataset.discountActive === 'true';
      this.bindChips();
      this.bindStickyBar();
      this.bindAddToCart();
    }

    /* --- Denomination chips: reflect selection + sync the bar price --- */
    bindChips() {
      this.radios = Array.from(this.querySelectorAll('[data-amount-radio]'));
      if (!this.radios.length) return;

      const sync = () => {
        this.radios.forEach((radio) => {
          const chip = radio.closest('.spring-package__chip');
          if (chip) chip.classList.toggle('is-selected', radio.checked);
          if (radio.checked && !this.discountActive && this.priceDisplay && radio.dataset.price) {
            this.priceDisplay.textContent = radio.dataset.price;
          }
        });
      };

      this.radios.forEach((radio) => radio.addEventListener('change', sync));
      sync();
    }

    /* --- Sticky action bar: pin to the viewport bottom once scrolled past --- */
    bindStickyBar() {
      const dock = this.querySelector('[data-bar-dock]');
      const bar = this.querySelector('[data-action-bar]');
      if (!dock || !bar) return;

      const update = () => {
        const stuck = dock.getBoundingClientRect().top < 0;
        if (stuck) dock.style.setProperty('--bar-h', bar.offsetHeight + 'px');
        dock.classList.toggle('is-stuck', stuck);
      };

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    }

    /* --- AJAX add-to-cart --- */
    bindAddToCart() {
      if (!this.form) return;
      const btn = this.form.querySelector('[data-add-to-cart]');
      const errorEl = this.querySelector('[data-cart-error]');

      this.form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (errorEl) errorEl.textContent = '';
        if (btn) {
          btn.classList.add('is-loading');
          btn.setAttribute('aria-busy', 'true');
        }

        try {
          const formData = new FormData(this.form);

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });
          const added = await addRes.json();
          if (!addRes.ok) {
            throw new Error(added.description || added.message || 'Could not add to cart.');
          }

          const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
          const addedItems = (added.items || [added]).map((i) => ({
            variant_id: i.variant_id,
            quantity: i.quantity,
          }));

          document.dispatchEvent(
            new CustomEvent('cart:changed', { detail: { cartData: { ...cart, items_added: addedItems } } })
          );
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: added } }));

          const drawerTrigger = document.querySelector("[data-action='show-cart-drawer']");
          if (drawerTrigger) {
            drawerTrigger.click();
          } else {
            window.location.href = '/cart';
          }
        } catch (err) {
          if (errorEl) errorEl.textContent = err.message || 'Could not add to cart.';
        } finally {
          if (btn) {
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-busy');
          }
        }
      });
    }
  }

  customElements.define('spring-package', SpringPackage);
})();
